import {skipToken} from '@reduxjs/toolkit/query'
import {useEffect} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'

import {subscribeOnBuildTypeEvents} from '../../../../../../../utils/subscriber'
import {
  BUILD_CHANGED_STATUS,
  BUILD_FINISHED,
  BUILD_INTERRUPTED,
  BUILD_STARTED,
  BUILD_TYPE_ADDED_TO_QUEUE,
  BUILD_TYPE_REMOVED_FROM_QUEUE,
} from '../../../../../../../utils/subscriptionEvents'
import {usePipelineJobArg, usePipelineRunArg} from '../../../PipelineRunPage.hooks'

import {isReused} from 'src/components/pages/BuildPage/DependenciesTab/DependenciesChain/DependenciesChain.utils'
import {getBuildStatusTypeFromBuild} from 'src/selectors'
import {restApi} from 'src/services/rest'
import type {BuildId, NormalizedBuild} from 'src/types'

export function useJobDetailsHeader(jobId: BuildId) {
  const location = useLocation()
  const navigate = useNavigate()

  const arg = usePipelineRunArg()
  const {run, job} = restApi.endpoints.getBuildNormalized.useQuery(arg, {
    selectFromResult: ({data}) => {
      const run = data?.entities?.builds?.[data?.result!]

      return {
        run,
        job: run?.['snapshot-dependencies']?.build?.find(build => build.id === jobId),
      }
    },
  })

  return {
    navigate,
    location,
    jobName: job?.buildType?.name,
    isJobRunning: job?.state === 'running',
    isJobQueued: job?.state === 'queued',
    jobStatus: getBuildStatusTypeFromBuild(job),
    isJobReused: isReused(job as NormalizedBuild, run),
  }
}

export function useParallelizedJobDetailsHeader(batchJobId: BuildId | null) {
  const arg = usePipelineJobArg()
  const {job, batchJob, refetch} = restApi.endpoints.getBuildNormalized.useQuery(
    batchJobId != null ? arg : skipToken,
    {
      selectFromResult: ({data}) => {
        const job = data?.entities?.builds?.[data?.result!]

        return {
          job,
          batchJob: job?.['snapshot-dependencies']?.build?.find(build => build.id === batchJobId),
        }
      },
    },
  )

  const internalId = batchJob?.buildType?.internalId

  useEffect(
    () =>
      internalId != null
        ? subscribeOnBuildTypeEvents(
            internalId,
            [
              BUILD_TYPE_ADDED_TO_QUEUE,
              BUILD_TYPE_REMOVED_FROM_QUEUE,
              BUILD_STARTED,
              BUILD_FINISHED,
              BUILD_INTERRUPTED,
              BUILD_CHANGED_STATUS,
            ],
            refetch,
          )
        : undefined,
    [internalId, refetch],
  )

  return {
    isBatchJobRunning: batchJob ? batchJob.state === 'running' : null,
    isBatchJobQueued: batchJob ? batchJob.state === 'queued' : null,
    batchJobStatus: batchJob ? getBuildStatusTypeFromBuild(batchJob) : null,
    isBatchJobReused: batchJob ? isReused(batchJob as NormalizedBuild, job) : null,
  }
}
